<template>
  <div class="add-missing-umr">
    <a href="#" class="add-missing-umr__link" @click.prevent="openModal">+ Add missing UMR</a>

    <BaseModal v-model="showModal" title="Add Missing UMR" size="lg" dialog-class="add-missing-umr-dialog">
      <ValidationObserver v-slot="{ handleSubmit }" ref="addMissingUmrObserver">
        <!-- Claim-ref mismatch error only (shown at the top). No passive
             instructional/hint text - the fields speak for themselves. -->
        <div v-if="claimRefError" class="add-missing-umr__msg add-missing-umr__msg--brand mb-3">
          {{ claimRefError }}
        </div>
        <b-row class="row">
          <b-col lg="12">
            <CashCallAutocompleteInput
                v-model="key.umr"
                label="UMR / Reference"
                required
                rules="required"
                placeholder="e.g. B0429BA2100999"
                :options="optionsFor('umr')"
                :max-options="1000"
                sort-options
                @focus="setActiveField('umr')"
                @blur="clearActiveField('umr')"
                @select="onUmrSelect"
            />
          </b-col>
        </b-row>
        <b-row class="row mt-3">
          <!-- Order: UMR (above), then Risk Code, CAT Code, Section #, Year of
               Account. Risk Code is NOT required (per LIQ ticket). -->
          <b-col lg="3">
            <CashCallAutocompleteInput
                v-model="key.riskCode"
                label="Risk Code"
                :disabled="matchedLock"
                :options="optionsFor('risk_code')"
                :max-options="1000"
                sort-options
                @focus="setActiveField('risk_code')"
                @blur="clearActiveField('risk_code')"
            />
          </b-col>
          <b-col lg="3">
            <!-- CAT Code: same styled autocomplete component as Risk Code, so
                 the dropdown + scrollbar match the app design (not the raw OS
                 datalist). Options are the distinct CAT codes from the fetched
                 UMR cache (catCodeOptions - cleaned of the "0"/date placeholders
                 that were leaking in). Free typing is still allowed, so a
                 brand-new code can be entered. Optional field. -->
            <CashCallAutocompleteInput
                v-model="key.catCode"
                label="CAT Code"
                :disabled="matchedLock"
                :options="catCodeOptions"
                :max-options="1000"
                sort-options
            />
            <small class="text-muted">(optional)</small>
          </b-col>
          <b-col lg="3">
            <CashCallAutocompleteInput
                v-model="key.sectionNo"
                label="Section #"
                required
                rules="required"
                :disabled="matchedLock"
                :options="optionsFor('section_no')"
                :max-options="1000"
                sort-options
                @focus="setActiveField('section_no')"
                @blur="clearActiveField('section_no')"
            />
          </b-col>
          <b-col lg="3">
            <CashCallAutocompleteInput
                v-model="key.yearOfAccount"
                label="Year of Account"
                required
                rules="required"
                :disabled="matchedLock"
                :options="optionsFor('year_of_account')"
                @focus="setActiveField('year_of_account')"
                @blur="clearActiveField('year_of_account')"
            />
          </b-col>
        </b-row>
        <b-row class="row">
          <b-col lg="6">
            <!-- Currency is a dropdown of the currencies supported in our
                 system (BDX_CURRENCY_TYPES), same list as the single-key flow. -->
            <BaseSelect
                v-model="key.currency"
                :options="CURRENCY_OPTIONS"
                required
                rules="required"
                size="sm"
                :disabled="currencyLocked || matchedLock"
            >
              <template v-slot:label>
                Currency <span class="text-danger">*</span>
              </template>
              <template v-slot:first>
                <b-form-select-option :value="''" disabled>Select Currency</b-form-select-option>
              </template>
            </BaseSelect>
          </b-col>
          <b-col lg="6">
            <!-- When a claim ref is already locked (other UMRs added), it's
                 fixed and read-only - a co-lead cash call is for one claim.
                 When this is the FIRST UMR (nothing locked yet), the field is
                 editable so the user can set the claim ref here. -->
            <template v-if="lockedClaimRef">
              <label class="add-missing-umr__field-label">Claim Reference <span class="text-danger">*</span></label>
              <b-form-input
                  :value="lockedClaimRef"
                  readonly
                  disabled
                  class="add-missing-umr__locked"
              />
              <small class="text-muted">Fixed to the co-lead claim reference.</small>
            </template>
            <CashCallAutocompleteInput
                v-else
                v-model="key.claimRef"
                label="Claim Reference"
                required
                rules="required"
                :options="EMPTY_OPTIONS"
            />
          </b-col>
        </b-row>

        <b-row class="row mt-2">
          <b-col lg="6">
            <CashCallAutocompleteInput
                v-model="underwriter"
                name="Lead Underwriter"
                required
                rules="required"
                placeholder="e.g. Chubb"
                :disabled="matchedLock"
                :options="optionsFor('underwriter')"
                :max-options="1000"
                sort-options
                @focus="setActiveField('underwriter')"
                @blur="clearActiveField('underwriter')"
            >
              <template v-slot:label>
                Lead Underwriter <span class="text-danger">*</span>
                <b-icon icon="info-circle" v-b-tooltip.hover="'Type to search underwriters, or enter a new one'" />
              </template>
            </CashCallAutocompleteInput>
            <!-- Brand-new underwriter (typed name not in the system list):
                 not a LimeIQ client, so the cash call can't be sent to them. -->
            <small v-if="underwriterIsNew" class="add-missing-umr__new-broker">
              Brand New to LimeIQ, will be unable to send to this recipient only as not a LimeIQ client.
            </small>
          </b-col>
          <b-col lg="6">
            <!-- Broker is the ONE field that stays editable on a matched UMR when
                 the matched UMR carries NO broker: the user then picks one from
                 the system broker dropdown. If the matched UMR already has a
                 broker, it locks like the rest (brokerLockedByMatch). -->
            <CashCallAutocompleteInput
                v-model="broker"
                name="Broker"
                required
                rules="required"
                placeholder="e.g. Amwins"
                :disabled="brokerLockedByMatch"
                :options="optionsFor('broker')"
                :max-options="1000"
                sort-options
                @focus="setActiveField('broker')"
                @blur="clearActiveField('broker')"
            >
              <template v-slot:label>
                Broker <span class="text-danger">*</span>
                <b-icon icon="info-circle" v-b-tooltip.hover="'Type to search brokers, or enter a new one'" />
              </template>
            </CashCallAutocompleteInput>
            <!-- Brand-new broker (typed name not in the system list). Same
                 wording as the new-underwriter message (LIQ-2414). -->
            <small v-if="brokerIsNew" class="add-missing-umr__new-broker">
              Brand New to LimeIQ, will be unable to send to this recipient only as not a LimeIQ client.
            </small>
          </b-col>
        </b-row>
        <b-row class="row">
          <b-col lg="6">
            <CashCallAutocompleteInput
                v-model="coverholder"
                name="Coverholder"
                required
                rules="required"
                :disabled="partiesAutofilled || matchedLock"
                :options="optionsFor('coverholder')"
                @focus="setActiveField('coverholder')"
                @blur="clearActiveField('coverholder')"
            >
              <template v-slot:label>
                Coverholder <span class="text-danger">*</span>
                <b-icon icon="info-circle" v-b-tooltip.hover="partiesAutofilled ? 'Carried from the first key - shared across the co-lead' : 'Type to search coverholders'" />
              </template>
            </CashCallAutocompleteInput>
          </b-col>
          <b-col lg="6">
            <CashCallAutocompleteInput
                v-model="tpa"
                name="TPA"
                required
                rules="required"
                :disabled="isTpaUser || partiesAutofilled || matchedLock"
                :options="optionsFor('tpa')"
                @focus="setActiveField('tpa')"
                @blur="clearActiveField('tpa')"
            >
              <template v-slot:label>
                TPA <span class="text-danger">*</span>
                <b-icon icon="info-circle" v-b-tooltip.hover="partiesAutofilled ? 'Carried from the first key - shared across the co-lead' : 'Type to search TPAs'" />
              </template>
            </CashCallAutocompleteInput>
          </b-col>
        </b-row>

        <div v-if="duplicateError" class="text-danger small mt-2">
          This UMR/key combination has already been added.
        </div>

        <div class="d-flex justify-content-end mt-3">
          <BaseButton type="button" variant="secondary" size="sm" @click="closeModal">Cancel</BaseButton>
          <BaseButton type="button" size="sm" class="ml-2" @click="handleSubmit(onSave)">Add to Cash Call</BaseButton>
        </div>
      </ValidationObserver>
    </BaseModal>
  </div>
</template>

<script>
import Vue from 'vue'
import { defineComponent, ref, computed, watch, nextTick, getCurrentInstance } from '@vue/composition-api'
import { ValidationObserver } from 'vee-validate'
import { debounce } from 'lodash-es'
import { buildManualBdxKey, isDuplicateBdxKey, facetedOptionsForField } from '../../util/cashCallCoLead'

import { BaseModal, BaseInput, BaseButton, BaseSelect } from '@components/base'
import { BDX_CURRENCY_TYPES } from '@constants'
import CashCallAutocompleteInput from './CashCallAutocompleteInput.vue'
import CashCallService from '../../services/CashCallService'

// A stable empty-array reference for fields with no active suggestions, so
// CashCallAutocompleteInput's own internal computed doesn't see a "changed"
// options prop (a fresh [] literal) on every render and recompute for nothing.
const EMPTY_OPTIONS = []

export default defineComponent({
  name: 'CashCallAddMissingUmrModal',
  components: {
    ValidationObserver,
    BaseModal,
    BaseInput,
    BaseButton,
    BaseSelect,
    CashCallAutocompleteInput
  },
  props: {
    existingKeys: {
      type: Array,
      default: () => []
    },
    // The claim ref fixed by the first added co-lead UMR (empty if none yet).
    // A co-lead cash call is for ONE claim, so a manually-added UMR must carry
    // this same claim ref; otherwise it's rejected here (mirrors the search
    // add path's guard in the parent).
    lockedClaimRef: {
      type: String,
      default: ''
    },
    // The first co-lead row's parties. On "Yes" (LimeIQ-active underwriter),
    // TPA/Broker/Coverholder pre-fill from here (editable) since a co-lead
    // shares them across keys.
    firstRecord: {
      type: Object,
      default: () => ({ tpa: '', broker: '', coverholder: '' })
    },
    // The currency shared across the co-lead, taken from the first SYSTEM UMR.
    // When set, a new manual UMR prefills its currency to this value.
    sharedCurrency: {
      type: String,
      default: ''
    },
    // True when the co-lead already contains a SYSTEM UMR (any matched row), so
    // the shared currency is established and this modal's currency must lock.
    currencyLocked: {
      type: Boolean,
      default: false
    },
    // Distinct underwriters in our system, offered as the Lead Underwriter
    // type-ahead suggestions (user may type any / pick a suggestion).
    underwriterOptions: {
      type: Array,
      default: () => []
    },
    // Distinct brokers in the system (Lincoln RLS 'Broker' groups), offered as
    // Broker type-ahead suggestions. Also used to detect a brand-new broker
    // (typed name not in this list -> "contact support" message).
    brokerOptions: {
      type: Array,
      default: () => []
    },
    isTpaUser: {
      type: Boolean,
      default: false
    },
    tpaCompanyName: {
      type: String,
      default: ''
    },
    // The already-flattened bulk GlobalUMR+claims rows (one per key+claim),
    // built ONCE by CashCallCreateModal. Passed in ready-to-use so this modal
    // never re-flattens the ~400k-row cache on mount - that flatten-on-mount
    // was the ~3s freeze when returning to step 2 (this modal is re-created
    // with the step). Every autocomplete field here is sourced from these rows.
    flattenedRows: {
      type: Array,
      default: () => []
    }
  },
  emits: ['save', 'update', 'redirect-to-claim-search'],
  setup(props, { emit }) {
    const instance = getCurrentInstance()
    const showModal = ref(false)
    // null = add mode (append a new row); a number = edit mode (replace the row
    // at that index in the parent's co-lead table).
    const editIndex = ref(null)

    const key = ref({ umr: '', sectionNo: '', riskCode: '', catCode: '', currency: '', claimRef: '', yearOfAccount: '' })


    // Canonical Risk Code list from the backend (cash_call_risk_code table),
    // fetched once when the modal first opens. Drives the Risk Code dropdown.
    const riskCodes = ref([])
    const fetchRiskCodes = async () => {
      if (riskCodes.value.length) return
      try {
        const resp = await CashCallService.getRiskCodes()
        const data = (resp && resp.data && resp.data.data) || []
        riskCodes.value = Array.isArray(data) ? data : []
      } catch (e) {
        riskCodes.value = []
      }
    }

    // Year of Account options: 2026..2010, MOST RECENT FIRST (2026 at the top of
    // the dropdown, descending to 2010). Type-ahead via CashCallAutocompleteInput.
    // The autocomplete keeps this source order (no sort-options on that field).
    const yearOfAccountOptions = Array.from(
      { length: 2026 - 2010 + 1 },
      (_, i) => String(2026 - i)
    )

    const underwriter = ref('')
    const broker = ref('')
    const coverholder = ref('')
    const tpa = ref('')

    const duplicateError = ref(false)
    const claimRefError = ref('')

    // Already flattened by the parent (see the flattenedRows prop) - no
    // re-flatten on mount. facetedOptionsForField below filters these rows,
    // but only on a debounced keystroke for the ONE focused field.
    const flattenedCache = props.flattenedRows

    const currentFilters = computed(() => ({
      umr: key.value.umr,
      risk_code: key.value.riskCode,
      cat_code: key.value.catCode,
      section_no: key.value.sectionNo,
      currency_type: key.value.currency,
      year_of_account: key.value.yearOfAccount,
      claim_ref: key.value.claimRef,
      underwriter: underwriter.value,
      tpa: tpa.value,
      broker: broker.value,
      coverholder: coverholder.value
    }))

    // Only ever compute suggestions for whichever ONE field currently has
    // focus, not all eleven at once on every keystroke (or on open, before
    // anything is even focused) - that's the difference between one filter
    // pass over the cache per keystroke and eleven, which is what made this
    // feel slow once the cache holds real production-sized data.
    const activeField = ref(null)
    const activeFieldOptions = ref(EMPTY_OPTIONS)

    const setActiveField = (field) => { activeField.value = field }
    const clearActiveField = (field) => {
      if (activeField.value === field) activeField.value = null
    }

    // Whether the typed UMR matches at least one fetched key (i.e. the system
    // "finds" this UMR). Drives whether Risk Code narrows to that UMR's codes or
    // falls back to the full fetched set.
    const umrMatchesCache = computed(() => {
      const umrVal = String(key.value.umr || '').trim()
      if (!umrVal) return false
      return flattenedCache.some((r) => String((r && r.umr) || '').trim() === umrVal)
    })

    // A Lloyd's risk code is a short alphanumeric token (e.g. "7T", "A2", "CY",
    // "T3"). Lincoln's bulk data carries dirty placeholder values in the
    // risk_code column - pure numbers ("0", "1"), datetimes ("2024-06-01
    // 00:00:00"), and blanks - which were polluting the dropdown. Keep only
    // values that look like a real risk code: 1-4 chars, at least one letter,
    // alphanumeric only. Everything else is dropped.
    const isValidRiskCode = (value) => {
      const v = String(value == null ? '' : value).trim()
      if (!v || v.length > 4) return false
      if (!/[A-Za-z]/.test(v)) return false      // must contain a letter
      return /^[A-Za-z0-9]+$/.test(v)             // alphanumeric only (no spaces/colons/dashes)
    }

    // ALL distinct valid risk codes across the keys fetched at the start, sorted.
    const allRiskCodes = computed(() => {
      const seen = new Set()
      for (const row of flattenedCache) {
        const rc = row && row.risk_code
        if (isValidRiskCode(rc)) {
          seen.add(String(rc).trim().toUpperCase())
        }
      }
      return Array.from(seen).sort()
    })

    // A real CAT code is a short alphanumeric token (e.g. "21F", "20R", "CORO",
    // "2019/CORO"). Lincoln's bulk data uses "0" as the "not specified"
    // placeholder; drop it and blanks. We deliberately DON'T over-filter (CAT
    // codes vary: with letters, digits, slashes) - just require a non-empty,
    // non-"0" value so genuine codes all appear.
    const isValidCatCode = (value) => {
      const v = String(value == null ? '' : value).trim()
      if (!v || v === '0') return false
      // Exclude anything that looks like a date (defends against any stray
      // date value that might sit in the cat_code column).
      if (/^\d{4}-\d{2}-\d{2}/.test(v)) return false
      if (/^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$/.test(v)) return false
      return true
    }

    // ALL distinct valid CAT codes across the fetched UMR cache, as a plain
    // string list for the styled CashCallAutocompleteInput (same shape as the
    // other autocomplete option lists). This is a STATIC full list (not the
    // focus-driven faceted recompute), so it never touches the machinery that
    // was cross-filtering CAT against a non-matching UMR - and it stays a real
    // input, so a brand-new code can still be typed.
    const catCodeOptions = computed(() => {
      const seen = new Set()
      for (const row of flattenedCache) {
        const cc = row && row.cat_code
        if (isValidCatCode(cc)) seen.add(String(cc).trim())
      }
      return Array.from(seen).sort((a, b) => a.localeCompare(b))
    })

    // Risk Code options by design:
    //  - UMR MATCHES the system -> show only the risk codes associated with that
    //    UMR (faceted, via activeFieldOptions).
    //  - UMR is genuinely MISSING (no match) -> fall back to ALL risk codes
    //    fetched from the keys at the start, so the user still has a list.
    const riskCodeOptions = computed(() => {
      // Always show the canonical Risk Code list (cash_call_risk_code table),
      // regardless of matched vs manual UMR. Falls back to the cache-derived
      // list only if the canonical fetch hasn't returned yet.
      if (riskCodes.value.length) return riskCodes.value
      if (!umrMatchesCache.value) return allRiskCodes.value
      return activeFieldOptions.value
        .filter(isValidRiskCode)
        .map((rc) => String(rc).trim().toUpperCase())
    })

    // Every field offers editable type-ahead suggestions. Year of Account offers
    // the fixed 2010-2027 list; Lead Underwriter and Broker suggest from the
    // system company lists; Section/CAT/UMR suggest from the fetched-keys cache
    // faceted by the values already chosen; Risk Code narrows to the matched
    // UMR's codes, or the full fetched set when the UMR is missing (see above).
    // All remain editable, so a genuinely missing UMR's values can still be typed.
    const optionsFor = (field) => {
      if (activeField.value !== field) return EMPTY_OPTIONS
      // Broker / Underwriter come from the SAME system lists the normal cash
      // call uses (LincolnRLSGroupsService.list('Broker') /
      // CashCallService.getLeadUnderwriters), passed in as props by the parent.
      if (field === 'underwriter') return props.underwriterOptions
      if (field === 'broker') return props.brokerOptions
      if (field === 'year_of_account') return yearOfAccountOptions
      if (field === 'risk_code') return riskCodeOptions.value
      // UMR always suggests existing system UMRs (so the user can find/pick one).
      if (field === 'umr') return activeFieldOptions.value
      // Section # and CAT Code: always suggest the distinct system values (a
      // type-ahead dropdown from whatever exists in the system), the same as the
      // other faceted fields - not gated on the entered UMR matching a system
      // UMR. The values still stay editable, so a genuinely new UMR's section /
      // cat code can be typed by hand if it isn't in the list.
      if (['section_no', 'cat_code'].includes(field)) {
        return activeFieldOptions.value
      }
      return EMPTY_OPTIONS
    }

    // Coverholder & TPA are locked when they've been AUTOFILLED from the first
    // key (i.e. a first record exists to carry them from). On a brand-new cash
    // call - when this is the FIRST key and there's nothing to carry from - they
    // stay editable so the user sets them; whatever they pick then transposes to
    // later keys. (TPA is additionally locked for a TPA-role user.)
    const partiesAutofilled = computed(
      () => Boolean(props.firstRecord && props.firstRecord.exists)
    )

    // Currency lock now comes from the parent via the `currencyLocked` prop: it
    // is true as soon as ANY UMR in the co-lead came from the system (which
    // establishes the shared currency), regardless of add order. When no system
    // UMR exists yet, currency stays SELECTABLE (starts empty), like TPA and
    // Coverholder in that case.

    // When the entered UMR MATCHES a UMR in the system, every field is
    // auto-filled from that UMR and locked (read-only) - the user picked a real
    // system UMR, so its details are authoritative and must not be edited. The
    // ONE exception is Broker: if the matched UMR carries NO broker, the Broker
    // field stays editable (a dropdown of system brokers) so the user can supply
    // one; if the matched UMR already has a broker, Broker is locked too. A
    // genuinely MISSING UMR (no cache match) leaves everything editable so the
    // user can type the new key's details.
    const matchedLock = computed(() => umrMatchesCache.value)
    // Whether the matched UMR already carries a broker (locks Broker); if it
    // doesn't, Broker stays open even on a matched UMR.
    const brokerLockedByMatch = computed(
      () => matchedLock.value && String(broker.value || '').trim() !== ''
    )

    // A broker is "brand new to LimeIQ" when the TYPED name isn't in the system
    // broker list - drives the "Brand New to LimeIQ..." message.
    // The broker CARRIED from the first UMR (fr.broker) is never "new": it came
    // from a real system UMR already on the co-lead, even if that name isn't in
    // the RLS "Broker" group list, so it must not be flagged. Only a broker the
    // user actually typed that's unknown to the system counts as new.
    const brokerIsNew = computed(() => {
      const val = String(broker.value || '').trim().toLowerCase()
      if (!val) return false
      const carried = String((props.firstRecord && props.firstRecord.broker) || '')
        .trim().toLowerCase()
      if (val === carried) return false
      const known = (props.brokerOptions || []).map((b) => String(b || '').trim().toLowerCase())
      return !known.includes(val)
    })

    // An underwriter is "new to LimeIQ" when the typed name isn't in the system
    // underwriter list. Persisted (as underwriter_limeiq_active=false) so the
    // recipients step knows this UMR's underwriter is brand-new.
    const underwriterIsNew = computed(() => {
      const val = String(underwriter.value || '').trim().toLowerCase()
      if (!val) return false
      const known = (props.underwriterOptions || []).map((u) => String(u || '').trim().toLowerCase())
      return !known.includes(val)
    })

    // NOTE: a brand-new Lead Underwriter or Broker shows a message (see the
    // template) but does NOT block "Add to Cash Call" - the user is allowed to
    // add the UMR anyway. The new-underwriter/new-broker consequence is handled
    // later at the Recipients/Send stage, not here.

    // Found-UMR auto-populate: when the typed UMR matches key(s) in the fetched
    // cache, fill any field that has a SINGLE distinct value for that UMR (its
    // parties, currency, year, etc.). Fields with several values for the UMR
    // (e.g. multiple risk codes / sections) are left for the user to pick from
    // their now-UMR-filtered dropdowns. Broker is only auto-filled when it's
    // still empty (per the ticket: found UMR locks everything except an empty
    // Broker). Only runs when the UMR actually matches - a genuinely missing
    // UMR (no cache match) leaves all fields as the user entered them.
    const AUTO_FILL_MAP = [
      ['section_no', (v) => { key.value.sectionNo = v }],
      ['risk_code', (v) => { key.value.riskCode = v }],
      ['cat_code', (v) => { key.value.catCode = v }],
      ['currency_type', (v) => { key.value.currency = v }],
      ['year_of_account', (v) => { key.value.yearOfAccount = v }],
      ['underwriter', (v) => { underwriter.value = v }],
      ['coverholder', (v) => { coverholder.value = v }],
      ['tpa', (v) => { if (!props.isTpaUser) tpa.value = v }],
    ]
    const autoPopulateFromUmr = () => {
      const umrVal = String(key.value.umr || '').trim()
      if (!umrVal) return
      const rows = flattenedCache.filter(
        (r) => String((r && r.umr) || '').trim() === umrVal
      )
      if (!rows.length) return  // genuinely missing UMR - leave everything manual
      const distinct = (field) => [...new Set(
        rows.map((r) => r[field]).filter((v) => v !== undefined && v !== null && String(v).trim() !== '')
      )]
      for (const [field, setter] of AUTO_FILL_MAP) {
        const vals = distinct(field)
        if (vals.length === 1) setter(String(vals[0]))
      }
      // Broker: the selected UMR is authoritative. If the matched UMR carries a
      // single broker, use THAT broker - even overwriting the broker carried
      // from the first UMR (a selected UMR's own broker is what belongs on this
      // key). Only when the matched UMR has NO broker of its own do we leave
      // whatever's there (the carried broker, or blank for the user to pick).
      const brokers = distinct('broker')
      if (brokers.length === 1) {
        broker.value = String(brokers[0])
      }
    }
    // Auto-populate ONLY when the user explicitly SELECTS a UMR from the
    // dropdown (see onUmrSelect / @select), never on every keystroke - typing a
    // partial value that happens to match a system UMR (e.g. "2") must not
    // prefill or redirect.
    const onUmrSelect = () => {
      autoPopulateFromUmr()
    }

    const recomputeActiveFieldOptions = () => {
      // UMR, Section # and CAT Code suggest their FULL distinct system list -
      // not constrained by the other key fields. "Add missing UMR" often means
      // the entered UMR isn't in the system yet, so cross-filtering section/cat
      // against a non-matching UMR would (wrongly) yield an empty dropdown.
      // These three always show every value in the system so the type-ahead
      // works; values stay editable for genuinely new entries. Every OTHER field
      // keeps full faceted cross-filtering.
      let filters = currentFilters.value
      if (['umr', 'section_no', 'cat_code'].includes(activeField.value)) {
        filters = {}
      }
      activeFieldOptions.value = activeField.value
        ? facetedOptionsForField(flattenedCache, filters, activeField.value)
        : EMPTY_OPTIONS
    }
    const recomputeActiveFieldOptionsDebounced = debounce(recomputeActiveFieldOptions, 150)

    // Switching focus to a different field should feel instant.
    watch(activeField, recomputeActiveFieldOptions)
    // Typing should debounce, so fast typing doesn't re-scan the cache on
    // every single keystroke.
    watch(currentFilters, () => {
      if (activeField.value) recomputeActiveFieldOptionsDebounced()
    })

    // Seed the fields when the modal opens (the Yes/No question is gone - fields
    // always show as editable suggestions):
    //  - Claim Reference is fixed to the locked claim ref (read-only in the
    //    template) so a co-lead UMR can't be added under a different claim.
    //  - Coverholder / TPA / Broker prefill from the first record (the parties a
    //    co-lead shares). Coverholder & TPA then lock (see partiesAutofilled);
    //    Broker stays editable. When there's no first record (the first key),
    //    everything stays editable so the user sets them and they transpose on.
    const seedFromFirstRecord = () => {
      key.value.claimRef = props.lockedClaimRef || key.value.claimRef
      const fr = props.firstRecord || {}
      if (!props.isTpaUser) tpa.value = fr.tpa || ''
      broker.value = fr.broker || ''
      coverholder.value = fr.coverholder || ''
      // Currency: when the co-lead already has a SYSTEM UMR, its currency is
      // shared and locked, so force it to the shared currency. Otherwise prefill
      // from the first record when empty (editable, user can change it).
      if (props.currencyLocked && props.sharedCurrency) {
        key.value.currency = props.sharedCurrency
      } else if (!key.value.currency) {
        key.value.currency = props.sharedCurrency || fr.currency_type || ''
      }
      // Lead Underwriter is carried from the first UMR too (as a convenience
      // prefill), but - unlike TPA/Broker/Coverholder - it stays EDITABLE: a
      // co-lead can legitimately have a different lead underwriter per UMR, so
      // the user can change it. A later autoPopulateFromUmr (matched UMR) will
      // overwrite it with that UMR's own underwriter.
      underwriter.value = fr.underwriter || ''

      // Per LIQ feedback: prefill EVERYTHING except the UMR from the first claim
      // ref selected. Risk Code / CAT Code / Section # / Year of Account carry
      // from the first record when empty; they stay EDITABLE so the user can
      // change them for a genuinely different missing UMR.
      if (!key.value.riskCode) key.value.riskCode = fr.risk_code || ''
      if (!key.value.catCode) key.value.catCode = fr.cat_code || ''
      if (!key.value.sectionNo) key.value.sectionNo = fr.section_no || ''
      if (!key.value.yearOfAccount) key.value.yearOfAccount = fr.year_of_account || ''
    }

    // Resolves the numeric id Lincoln's own key-building convention needs
    // (rls_prefix + "-" + id) for a cache-autocompleted TPA/Coverholder name -
    // the cache carries both the name and its id on the same flattened row.
    const findIdForField = (fieldName, idFieldName, value) => {
      if (!value) return null
      const match = flattenedCache.find((row) => row[fieldName] === value && row[idFieldName] != null)
      return match ? match[idFieldName] : null
    }

    const openModal = () => {
      editIndex.value = null
      if (props.isTpaUser) {
        // Lincoln's bulk endpoint already scopes every row to the caller's
        // own TPA for a TPA-role user, so any row's tpa/tpa_id is theirs.
        const ownRow = flattenedCache.find((row) => row.tpa)
        tpa.value = (ownRow && ownRow.tpa) || props.tpaCompanyName
      }
      seedFromFirstRecord()
      showModal.value = true
    }

    // Opens the modal in EDIT mode, pre-filled with an existing co-lead row's
    // values. Called by the parent (the row's "Edit" button). On save, the modal
    // emits `update` with editIndex so the parent replaces that row in place
    // rather than adding a new one.
    const openForEdit = (row, rowIndex) => {
      editIndex.value = rowIndex
      const firstBroker = Array.isArray(row.broker) ? (row.broker[0] || '') : (row.broker || '')
      key.value = {
        umr: row.umr || '',
        sectionNo: row.sn || '',
        riskCode: row.rc || '',
        catCode: row.cc || '',
        currency: row.currency_type || '',
        claimRef: (row.claimref && row.claimref[0]) || props.lockedClaimRef || '',
        yearOfAccount: row.year_of_account || ''
      }
      underwriter.value = row.underwriter_lead || ''
      broker.value = firstBroker
      coverholder.value = row.coverholder || ''
      tpa.value = row.tpa || ''
      showModal.value = true
    }

    const resetState = () => {
      key.value = { umr: '', sectionNo: '', riskCode: '', catCode: '', currency: '', claimRef: '', yearOfAccount: '' }
      underwriter.value = ''
      broker.value = ''
      coverholder.value = ''
      tpa.value = ''
      duplicateError.value = false
      claimRefError.value = ''
      activeField.value = null
      activeFieldOptions.value = EMPTY_OPTIONS
      editIndex.value = null
      recomputeActiveFieldOptionsDebounced.cancel()
      // Clear vee-validate's error state so required-field messages don't flash
      // for a frame as the modal closes/reopens.
      const proxy = instance && instance.proxy
      const observer = proxy && proxy.$refs && proxy.$refs.addMissingUmrObserver
      if (observer && typeof observer.reset === 'function') observer.reset()
    }

    const closeModal = () => {
      showModal.value = false
    }

    // BaseModal's v-model also closes via BootstrapVue's own X icon/Esc key/
    // backdrop click, all of which set showModal straight to false and skip
    // closeModal() entirely - watching showModal itself is the only way to
    // guarantee state resets no matter how the modal was dismissed.
    watch(showModal, (isOpen) => {
      if (isOpen) fetchRiskCodes()
      if (!isOpen) resetState()
    })

    // Risk Code/CAT Code/Section # blank -> "0", so a manually-built key
    // never ends up with an empty segment (e.g. two underscores in a row)
    // and always has all 6 base parts, matching Lincoln's own key shape.
    const orZero = (value) => (value === '' || value === null || value === undefined ? '0' : value)

    // When the entered UMR MATCHES a UMR already in the system, it is NOT missing
    // - so it must not be added through this modal. Show a simple message, close
    // the modal, and send the user back to the Claim Reference search (via the
    // parent) to add it there the normal way.
    const redirectMatchedUmrToClaimSearch = () => {
      const umrVal = String(key.value.umr || '').trim()
      Vue.prototype.$toasted && Vue.prototype.$toasted.show(
        `UMR ${umrVal} is already in the system — please add it from the Claim Reference search above.`,
        { duration: 5000 }
      )
      closeModal()
      // Tell the parent to focus/scroll to the Claim Reference search box.
      emit('redirect-to-claim-search')
    }


    const onSave = () => {
      // If the entered UMR MATCHES a UMR already in the system, it isn't
      // "missing" - don't add it here. Redirect the user to the Claim Reference
      // search to add it the normal way.
      if (umrMatchesCache.value) {
        redirectMatchedUmrToClaimSearch()
        return
      }

      const riskCode = orZero(key.value.riskCode)
      const catCode = orZero(key.value.catCode)
      const sectionNo = orZero(key.value.sectionNo)

      const resolvedTpaId = findIdForField('tpa', 'tpa_id', tpa.value)
      const resolvedCoverholderId = findIdForField('coverholder', 'coverholder_id', coverholder.value)

      const bdxKey = buildManualBdxKey({
        umr: key.value.umr,
        riskCode,
        catCode,
        sectionNo,
        currency: key.value.currency,
        yearOfAccount: key.value.yearOfAccount,
        tpaCompany: { id: resolvedTpaId, name: tpa.value },
        coverholderCompany: { id: resolvedCoverholderId, name: coverholder.value }
      })

      // In edit mode the row keeps (or re-derives) its own key, so exclude that
      // row's original key from the duplicate check - otherwise editing a row and
      // saving it unchanged would flag it as a duplicate of itself.
      const dupPool = editIndex.value !== null
        ? (props.existingKeys || []).filter((_, i) => i !== editIndex.value)
        : props.existingKeys
      if (isDuplicateBdxKey(bdxKey, dupPool)) {
        duplicateError.value = true
        return
      }
      duplicateError.value = false

      // Claim Ref is fixed to the co-lead's locked claim ref (the field is
      // read-only), so it always matches. Fall back to the entered value only
      // for the very first UMR when nothing is locked yet.
      const effectiveClaimRef = String(props.lockedClaimRef || key.value.claimRef || '').trim()
      claimRefError.value = ''

      // A co-lead cash call is ONE claim, so a manually-added UMR inherits the
      // claim location (Country / Loss State / SL Tax State / Province) and NAIC
      // from the FIRST already-added UMR - whenever one exists - regardless of
      // the Yes/No answer. Only when this manual UMR is the FIRST/only key (no
      // prior record to copy) do those fields start empty for manual selection
      // on the Claim Details tab.
      const fr = props.firstRecord || {}
      const prefillLocation = !!fr.exists

      const payload = {
        bdx_key: [bdxKey],
        umr: key.value.umr,
        rc: riskCode,
        cc: catCode,
        sn: sectionNo,
        currency_type: key.value.currency,
        year_of_account: key.value.yearOfAccount,
        claimref: effectiveClaimRef ? [effectiveClaimRef] : [],
        tpa: tpa.value,
        underwriter_lead: underwriter.value,
        broker: broker.value ? [broker.value] : [],
        coverholder: coverholder.value,
        // A co-lead is ONE claim, so the insured is the same across every UMR -
        // inherit it from the first (system-sourced) record so the manual key's
        // summary isn't blank.
        insured_name: fr.insured_name || '',
        // A co-lead is ONE claim, so Cause of Loss is shared too - inherit it
        // from the first record (it's a shared Step 3 field) so a manual key
        // isn't blank on Review before Step 3 is visited. Editing it on Step 3
        // still fans out to every row via updateAllRows.
        cause_of_loss: prefillLocation ? (fr.cause_of_loss || '') : '',
        date_of_loss: null,
        date_claim_made: null,
        loss_country: prefillLocation ? (fr.loss_country || '') : '',
        loss_state: prefillLocation ? (fr.loss_state || '') : '',
        loss_sl_state: prefillLocation ? (fr.loss_sl_state || '') : '',
        canada_province: prefillLocation ? (fr.canada_province || '') : '',
        naic_code: prefillLocation ? (fr.naic_code || '') : '',
        // Share %/Funding Required are set and edited in the Co-Lead UMRs
        // table itself (every row already has those columns), not here -
        // default to the same ON/0 every row starts with.
        share_percentage: 0,
        funding_required: true,
        matched: false,
        // True when the underwriter IS a known system underwriter; false when
        // brand-new (typed name not in the system list). The recipients step
        // uses this to mark a UMR whose underwriter is new to LimeIQ.
        underwriter_limeiq_active: !underwriterIsNew.value,
        // Same for the broker: false when the typed broker isn't in the system
        // list (brand-new). Combined with matched===false (a manual UMR), the
        // recipients step BLOCKS send for a new-UMR-with-new-broker and shows
        // the "contact support" message. A new UMR with an EXISTING broker, or a
        // system UMR with no broker contacts, is NOT blocked.
        broker_limeiq_active: !brokerIsNew.value
      }

      if (editIndex.value !== null) {
        // Edit mode: replace the existing row in place. Preserve its current
        // Share % / Funding Required (those are edited in the table, not here).
        emit('update', { index: editIndex.value, row: payload })
      } else {
        emit('save', payload)
      }

      closeModal()
    }

    return {
      showModal,
      key,
      underwriter,
      broker,
      coverholder,
      tpa,
      CURRENCY_OPTIONS: BDX_CURRENCY_TYPES,
      EMPTY_OPTIONS,
      catCodeOptions,
      optionsFor,
      partiesAutofilled,
      matchedLock,
      brokerLockedByMatch,
      brokerIsNew,
      underwriterIsNew,
      setActiveField,
      clearActiveField,
      onUmrSelect,
      duplicateError,
      claimRefError,
      openModal,
      openForEdit,
      closeModal,
      onSave
    }
  }
})
</script>

<style scoped>
.add-missing-umr__link {
  display: inline-block;
  font-weight: 600;
  color: #1c4a50;
}

/* Claim-ref lock messages — brand green to match Search/Select, not red. */
.add-missing-umr__msg {
  font-size: 13px;
  border-radius: 6px;
  padding: 8px 12px;
}
.add-missing-umr__msg--brand {
  color: #fff;
  background-color: #1c4a50;
  font-weight: 600;
}
.add-missing-umr__msg--info {
  color: #4a3b00;
  background-color: #fff6d6;
  border: 1px solid #ffe08a;
}
.add-missing-umr__msg--info a {
  color: #1c4a50;
  font-weight: 600;
  text-decoration: underline;
}

/* Brand-new broker notice (typed broker not in the system list). */
.add-missing-umr__new-broker {
  display: block;
  margin-top: 4px;
  color: #4a3b00;
  background-color: #fff6d6;
  border: 1px solid #ffe08a;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
}
.add-missing-umr__new-broker a {
  color: #1c4a50;
  font-weight: 600;
  text-decoration: underline;
}

.add-missing-umr__field-label {
  display: block;
  margin-bottom: 4px;
  color: #525f7f;
}

/* Locked (read-only) Claim Reference — visually distinct as non-editable. */
.add-missing-umr__locked {
  background-color: #eef4f4 !important;
  color: #1c4a50 !important;
  font-weight: 600;
  cursor: not-allowed;
}

.add-missing-umr__q-label {
  display: block;
  font-weight: 600;
  color: #32325d;
  margin-bottom: 6px;
}

.add-missing-umr__q-options {
  display: flex;
  gap: 24px;
}

.add-missing-umr__q-option {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  cursor: pointer;
  font-weight: 500;
  color: #32325d;
}

.add-missing-umr__q-option input {
  cursor: pointer;
}
</style>

<style>
/* Unscoped: BootstrapVue renders b-modal's dialog outside this component's
   own template tree, so a scoped selector can't reach it - dialog-class
   passed to BaseModal/b-modal is the only reliable hook. */
.add-missing-umr-dialog {
  max-width: 750px;
}
</style>
